import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
import classNames from 'classnames';
import { m as motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigation } from 'swiper';
import type { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import type { SpeakersSectionRawProps } from '@/shared/components/sections/SpeakersSection';
import Button from '@/shared/components/shared/Button';
import Image from '@/shared/components/shared/Image';
import Pagination from '@/shared/components/shared/Pagination';
import Responsive from '@/shared/components/shared/Responsive';
import { useMounted } from '@/shared/hooks/use-mounted';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { easeOutQuart } from '@/shared/lib/utils/easings';
import getNoun from '@/shared/lib/utils/get-noun';

const DESKTOP_VISIBLE_COUNT = 12;
const TABLET_VISIBLE_COUNT = 8;
const ENABLE_HOVER_TOOLTIP = false;
const PREV_BTN = 'speakers-grid-section__prev-btn';
const NEXT_BTN = 'speakers-grid-section__next-btn';

type Speaker = NonNullable<SpeakersSectionRawProps['speakers']>[number];

type Props = React.HTMLAttributes<HTMLElement> & {
    speakersSectionProps: SpeakersSectionRawProps;
};

type SpeakerCardProps = {
    speaker: Speaker;
    index: number;
    isActive: boolean;
    isDesktop: boolean;
    sectionRef: React.RefObject<HTMLElement | null>;
    onActivate: (index: number) => void;
    onDeactivate: () => void;
};

const hasSpeakerContent = (speaker: Speaker) => Boolean(speaker.name || speaker.job);

function SpeakerCard({ speaker, index, isActive, isDesktop, sectionRef, onActivate, onDeactivate }: SpeakerCardProps) {
    const tpName = useTypograf(speaker.name ?? '');
    const tpJob = useTypograf(speaker.job ?? '');
    const interactive = ENABLE_HOVER_TOOLTIP && hasSpeakerContent(speaker);
    const mediaRef = useRef<HTMLDivElement | null>(null);
    const infoRef = useRef<HTMLDivElement | null>(null);
    const arrowRef = useRef<HTMLDivElement | null>(null);
    const floatingGenRef = useRef(0);

    const updateFloating = useCallback(async () => {
        const reference = mediaRef.current;
        const floating = infoRef.current;
        const arrowEl = arrowRef.current;

        if (!reference || !floating || !isDesktop || !isActive) {
            return;
        }

        const gen = floatingGenRef.current + 1;
        floatingGenRef.current = gen;

        const { x, y, placement, middlewareData } = await computePosition(reference, floating, {
            placement: 'bottom',
            strategy: 'fixed',
            middleware: [
                offset(12),
                flip({
                    padding: { top: 120, bottom: 16, left: 16, right: 16 },
                }),
                shift({ padding: 16 }),
                ...(arrowEl ? [arrow({ element: arrowEl, padding: 8 })] : []),
            ],
        });

        if (floatingGenRef.current !== gen || !infoRef.current) {
            return;
        }

        Object.assign(floating.style, {
            position: 'fixed',
            left: `${x}px`,
            top: `${y}px`,
        });

        if (arrowEl && middlewareData.arrow) {
            const { x: arrowX, y: arrowY } = middlewareData.arrow;
            const side = placement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right';
            const staticSide = {
                top: 'bottom',
                bottom: 'top',
                left: 'right',
                right: 'left',
            }[side];

            Object.assign(arrowEl.style, {
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide]: '-6px',
            });

            arrowEl.dataset.placement = side;
        }
    }, [isActive, isDesktop]);

    useLayoutEffect(() => {
        if (!isActive || !isDesktop) {
            if (infoRef.current) {
                infoRef.current.style.position = '';
                infoRef.current.style.left = '';
                infoRef.current.style.top = '';
            }

            return;
        }

        void updateFloating();
    }, [isActive, isDesktop, updateFloating]);

    useEffect(() => {
        if (!isActive || !isDesktop) {
            return;
        }

        let rafId = 0;
        const onReposition = () => {
            if (rafId) {
                return;
            }

            rafId = requestAnimationFrame(() => {
                rafId = 0;
                void updateFloating();
            });
        };

        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [isActive, isDesktop, updateFloating]);

    const info =
        ENABLE_HOVER_TOOLTIP && isDesktop && (tpName || tpJob) ? (
            <div ref={infoRef} className="speakers-grid-section__card-info speakers-grid-section__card-info--open">
                <div ref={arrowRef} className="speakers-grid-section__card-arrow" aria-hidden />
                <div className="speakers-grid-section__card-info-inner">
                    {tpName && <div className="speakers-grid-section__card-info-name text-l">{tpName}</div>}
                    {tpJob && <div className="speakers-grid-section__card-info-job text-xs">{tpJob}</div>}
                </div>
            </div>
        ) : null;

    return (
        <div
            className={classNames('speakers-grid-section__card', {
                'speakers-grid-section__card--interactive': interactive && isDesktop,
                'is-active': isActive,
            })}
            onMouseEnter={() => {
                if (isDesktop && interactive) {
                    onActivate(index);
                }
            }}
            onMouseLeave={() => {
                if (isDesktop) {
                    onDeactivate();
                }
            }}
        >
            <div ref={mediaRef} className="speakers-grid-section__card-media">
                {speaker.img?.src ? (
                    <Responsive className="speakers-grid-section__card-img">
                        <Image
                            src={speaker.img.src}
                            alt={speaker.img.alt || tpName || ''}
                            title={speaker.img.title}
                            width={speaker.img.width}
                            height={speaker.img.height}
                            sizes="(max-width: 767px) 54vw, (max-width: 1199px) 25vw, 16vw"
                        />
                    </Responsive>
                ) : (
                    <Responsive className="speakers-grid-section__card-img">
                        <div className="speakers-grid-section__card-placeholder" />
                    </Responsive>
                )}
            </div>
            {(tpName || tpJob) && (
                <div className="speakers-grid-section__card-text">
                    {tpName && <div className="speakers-grid-section__card-name text-m">{tpName}</div>}
                    {tpJob && <div className="speakers-grid-section__card-job text-xs">{tpJob}</div>}
                </div>
            )}
            {isActive && info && sectionRef.current ? createPortal(info, sectionRef.current) : null}
        </div>
    );
}

function SpeakersGridSection({ speakersSectionProps, className, ...props }: Props) {
    const { title, text, speakers } = speakersSectionProps;
    const tpTitle = useTypograf(title ?? '');
    const tpText = useTypograf(text ?? '');
    const items = speakers ?? [];
    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';
    const isMobile = device.includes('mobile');
    const isMounted = useMounted();
    const reduceMotion = useReducedMotion();
    const sectionRef = useRef<HTMLElement | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [paginationIndex, setPaginationIndex] = useState(0);
    const [lastPaginationIndex, setLastPaginationIndex] = useState(0);
    const [isSliderActive, setIsSliderActive] = useState(false);

    const visibleCount = isDesktop ? DESKTOP_VISIBLE_COUNT : TABLET_VISIBLE_COUNT;
    const visibleItems = items.slice(0, visibleCount);
    const extraItems = isExpanded ? items.slice(visibleCount) : [];
    const showButton = !isMobile && !isExpanded && items.length > visibleCount;
    const buttonLabel = `Показать всех ${items.length} ${getNoun(items.length, 'спикера', 'спикера', 'спикеров')}`;

    useEffect(() => {
        setActiveIndex(null);

        if (isMobile) {
            setIsExpanded(false);
            setPaginationIndex(0);
            setLastPaginationIndex(0);
            setIsSliderActive(false);
        }
    }, [isDesktop, isMobile]);

    if (items.length === 0 && !tpTitle && !tpText) {
        return null;
    }

    const renderCards = (list: Speaker[], keyOffset = 0) =>
        list.map((speaker, index) => (
            <li className="speakers-grid-section__item" key={keyOffset + index}>
                <SpeakerCard
                    speaker={speaker}
                    index={keyOffset + index}
                    isActive={activeIndex === keyOffset + index}
                    isDesktop={isDesktop}
                    sectionRef={sectionRef}
                    onActivate={setActiveIndex}
                    onDeactivate={() => setActiveIndex(null)}
                />
            </li>
        ));

    const handleSliderSync = (swiper: SwiperInstance) => {
        setIsSliderActive(!swiper.isLocked);
    };

    return (
        <section
            {...props}
            ref={sectionRef}
            className={classNames('speakers-grid-section js-header-theme-trigger', className)}
            data-theme="dark"
        >
            <div className="wrapper speakers-grid-section__top">
                {tpTitle && (
                    <div className="speakers-grid-section__title-wrapper">
                        <h2 className="speakers-grid-section__title h3">{tpTitle}</h2>
                    </div>
                )}
                {tpText && <div className="speakers-grid-section__text text-l">{tpText}</div>}
            </div>

            {items.length > 0 && !isMobile && (
                <div className="wrapper speakers-grid-section__bottom">
                    <ul className="list-unstyled speakers-grid-section__list">{renderCards(visibleItems)}</ul>

                    {extraItems.length > 0 && (
                        <motion.div
                            className="speakers-grid-section__extra"
                            initial={reduceMotion ? false : { height: 0 }}
                            animate={{ height: 'auto' }}
                            transition={{ duration: reduceMotion ? 0 : 0.6, ease: easeOutQuart }}
                        >
                            <ul className="list-unstyled speakers-grid-section__extra-list">
                                {renderCards(extraItems, visibleCount)}
                            </ul>
                        </motion.div>
                    )}

                    {showButton && (
                        <div className="speakers-grid-section__btn-wrap">
                            <Button
                                type="button"
                                variant="outline-dark"
                                className="speakers-grid-section__btn"
                                onClick={() => setIsExpanded(true)}
                            >
                                {buttonLabel}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {items.length > 0 && isMobile && (
                <div className="speakers-grid-section__slider-wrap">
                    {isMounted ? (
                        <Swiper
                            className="speakers-grid-section__slider"
                            modules={[Navigation]}
                            navigation={{
                                prevEl: `.${PREV_BTN}`,
                                nextEl: `.${NEXT_BTN}`,
                            }}
                            speed={650}
                            slidesPerView="auto"
                            watchOverflow
                            breakpoints={{
                                320: {
                                    slidesPerGroup: 1,
                                    spaceBetween: 8,
                                    slidesOffsetBefore: 16,
                                    slidesOffsetAfter: 16,
                                },
                                768: {
                                    slidesPerGroup: 1,
                                    spaceBetween: 16,
                                    slidesOffsetBefore: 22,
                                    slidesOffsetAfter: 22,
                                },
                            }}
                            onAfterInit={(swiper) => {
                                requestAnimationFrame(() => handleSliderSync(swiper));
                            }}
                            onResize={handleSliderSync}
                            onSlideChange={(swiper) => {
                                const perGroup =
                                    typeof swiper.params.slidesPerGroup === 'number' ? swiper.params.slidesPerGroup : 1;
                                setLastPaginationIndex(paginationIndex);
                                setPaginationIndex(Math.floor(swiper.realIndex / perGroup));
                            }}
                        >
                            {items.map((speaker, index) => (
                                <SwiperSlide className="speakers-grid-section__slide" key={index}>
                                    <SpeakerCard
                                        speaker={speaker}
                                        index={index}
                                        isActive={false}
                                        isDesktop={false}
                                        sectionRef={sectionRef}
                                        onActivate={() => undefined}
                                        onDeactivate={() => undefined}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        <div className="wrapper speakers-grid-section__slider-fallback">
                            {items.slice(0, 2).map((speaker, index) => (
                                <div className="speakers-grid-section__slide" key={index}>
                                    <SpeakerCard
                                        speaker={speaker}
                                        index={index}
                                        isActive={false}
                                        isDesktop={false}
                                        sectionRef={sectionRef}
                                        onActivate={() => undefined}
                                        onDeactivate={() => undefined}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {items.length > 1 && (
                        <Pagination
                            numSlides={items.length}
                            currentRealIndex={paginationIndex}
                            lastRealIndex={lastPaginationIndex}
                            prevElClass={`${PREV_BTN} slider-prev-btn`}
                            nextElClass={`${NEXT_BTN} slider-next-btn`}
                            wrapperClass={classNames('speakers-grid-section__pagination pagination-wrapper wrapper', {
                                'speakers-grid-section__pagination--inactive': !isSliderActive,
                            })}
                        />
                    )}
                </div>
            )}
        </section>
    );
}

export default SpeakersGridSection;
